<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import type { HTMLAttributes, InputHTMLAttributes } from 'vue'
import type { inputMaskingConfiguration } from '.'

const props = defineProps<{
  class?: HTMLAttributes['class']
  id?: string
  modelValue?: string | number
  autocomplete?: InputHTMLAttributes['autocomplete']
  inputmode?: HTMLAttributes['inputmode']
  inputClass?: HTMLAttributes['class']
  placeholder?: InputHTMLAttributes['placeholder']
  type?: InputHTMLAttributes['type']
  disabled?: InputHTMLAttributes['disabled']
  required?: boolean
  inputMask?: keyof typeof inputMaskingConfiguration
  maxLength?: HTMLInputElement['maxLength']
  allowEmoji?: boolean
  trailing?: string
  max?: InputHTMLAttributes['max']
  localTel?: boolean
  invalid?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value?: string | number): void
}>()

const slots = useSlots()
const content = useVModel(props, 'modelValue', emit)

const inputClasses = computed(() =>
  cn([
    {
      'pl-10': slots.leading,
      'pr-10': slots.trailing || props.trailing
    },
    props.inputClass
  ])
)
</script>

<template>
  <div :class="cn('relative', props.class)">
    <div
      v-if="slots.leading"
      class="absolute left-3.5 top-1/2 text-sm -translate-y-1/2"
    >
      <slot name="leading" />
    </div>
    <slot>
      <UiInputField
        :id="id"
        v-model="content"
        :class="inputClasses"
        :type="type"
        :max="max"
        :allow-emoji="allowEmoji"
        :placeholder="placeholder ?? ''"
        :required="required"
        :autocomplete="autocomplete"
        :max-length="maxLength"
        :maxlength="maxLength"
        :invalid="invalid"
        :inputmode="inputmode"
        :input-mask="inputMask"
        :local-tel="localTel"
        :disabled="disabled"
      />
    </slot>
    <div
      v-if="slots.trailing || props.trailing"
      class="absolute right-3.5 top-1/2 text-xs -translate-y-1/2 pointer-events-none"
    >
      <slot name="trailing">{{ props.trailing }}</slot>
    </div>
  </div>
</template>
